// src/firebase/auth.js
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getIdTokenResult,
} from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

let currentUserRole = null;
let currentUserEmail = null;

/**
 * 🔹 Try to fetch role from Firebase Custom Claims first.
 * If not set, fallback to Firestore users/{uid}.
 */
async function fetchUserRole(uid, email) {
  try {
    // ✅ Try from Auth custom claims
    const tokenResult = await getIdTokenResult(auth.currentUser, true);
    if (tokenResult.claims.role) {
      console.log(`✅ Auth claim role for ${email}: ${tokenResult.claims.role}`);
      return tokenResult.claims.role.toLowerCase();
    }

    // ✅ Fallback: Firestore users/{uid}
    const userRef = doc(db, "users", uid);
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      const data = snap.data();
      console.log(`✅ Firestore role (${data.role}) loaded for ${email}`);
      return (data.role || "").toLowerCase();
    }

    console.warn(`⚠️ No role found in Firestore for UID: ${uid}`);
    return "";
  } catch (err) {
    console.error("❌ Error fetching user role:", err.message);
    return "";
  }
}

/**
 * 🔐 React Admin Authentication Provider
 */
export const authProvider = {
  // ✅ LOGIN
  login: async ({ username, password }) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, username, password);
      const { uid, email } = cred.user;

      const role = await fetchUserRole(uid, email);
      if (!role) throw new Error("Role not found for user.");

      // Clear old cache
      localStorage.clear();
      currentUserRole = role;
      currentUserEmail = email;

      localStorage.setItem("role", role);
      localStorage.setItem("email", email);

      console.log(`✅ Login successful — ${email} (${role})`);
      return Promise.resolve();
    } catch (err) {
      console.error("❌ Login error:", err.message);
      throw new Error("Invalid credentials or missing role.");
    }
  },

  // ✅ LOGOUT
  logout: async () => {
    await signOut(auth);
    currentUserRole = null;
    currentUserEmail = null;
    localStorage.clear();
    console.log("👋 Logged out successfully.");
    return Promise.resolve();
  },

  // ✅ CHECK AUTH
  checkAuth: () =>
    new Promise((resolve, reject) => {
      const unsub = onAuthStateChanged(auth, async (user) => {
        unsub();
        if (!user) return reject();

        try {
          const role = await fetchUserRole(user.uid, user.email);
          if (!role) return reject();

          currentUserRole = role;
          currentUserEmail = user.email;

          localStorage.setItem("role", role);
          localStorage.setItem("email", user.email);

          console.log(`🔄 Auth check: ${user.email} (${role})`);
          resolve();
        } catch (err) {
          console.warn("⚠️ checkAuth failed:", err.message);
          reject();
        }
      });
    }),

  checkError: () => Promise.resolve(),

  // ✅ GET PERMISSIONS (used by React Admin)
getPermissions: () => {
  const cached = localStorage.getItem("role") || currentUserRole;
  return Promise.resolve((cached || "guest").toLowerCase());
},




  // ✅ GET IDENTITY
  getIdentity: async () => ({
    id: currentUserEmail || localStorage.getItem("email"),
    fullName: currentUserRole || "User",
  }),
};

/**
 * ✅ Helper: get current role from cache
 */
export function getCurrentRole() {
  return (currentUserRole || localStorage.getItem("role") || "").toLowerCase();
}

/**
 * ✅ Helper: refresh user role manually (safe + cached)
 */
export async function refreshUserRole() {
  const user = auth.currentUser;
  if (!user) return null;

  const cachedRole = localStorage.getItem("role");
  const cachedEmail = localStorage.getItem("email");

  // ✅ Skip re-fetching if same user already cached
  if (cachedRole && cachedEmail === user.email) {
    return cachedRole;
  }

  try {
    const role = await fetchUserRole(user.uid, user.email);
    if (!role) throw new Error("Role not found.");

    currentUserRole = role;
    currentUserEmail = user.email;

    localStorage.setItem("role", role);
    localStorage.setItem("email", user.email);

    console.log(`✅ Final confirmed role: ${role} for ${user.email}`);
    return role;
  } catch (err) {
    console.error("❌ Failed to refresh role:", err.message);
    return null;
  }
}
